/* Where a sign-up came from.
 *
 * The marketing site keeps each visitor's first and latest touch in an `ini_attr` cookie on
 * `.inistate.com`, and their choice in `ini_consent`; this app can read both. Everything here is
 * assembled once, at sign-up, and sent with the call that creates the login — the server stores it and
 * classifies it (docs/identity-signup-funnel-proposal.md §4.2.1).
 *
 * Three rules this file exists to keep:
 *   1. A missing or malformed cookie is a missing key, never an error. Nothing here may break a sign-up.
 *   2. Advertising identifiers travel only with advertising consent, analytics ids only with analytics
 *      consent. Without an `ini_consent` cookie the site's own default applies: opt-in inside Europe,
 *      granted elsewhere.
 *   3. Links that skip the marketing site — the n8n node's docs, an AI connector directory, a product
 *      e-mail — are the app's own touch, kept here because the site never saw them.
 */

import Vue from "vue";

/** The site writes this on `.inistate.com`; both cookies are readable from this host. */
const ATTR_COOKIE = "ini_attr";
const CONSENT_COOKIE = "ini_consent";
const REFERENCE_COOKIE = "onboarding_ref";

/** The app's own touch, kept for a month — long enough to outlive a "sign up later". */
const APP_TOUCH_KEY = "ini_app_attr";
const APP_TOUCH_DAYS = 30;

/** GA4 (the marketing property) and Meta cookie names. */
const GA_COOKIE = "_ga";
const GA_SESSION_COOKIE = "_ga_Z7S5BD8ETF";
const FBP_COOKIE = "_fbp";
const FBC_COOKIE = "_fbc";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_id"];

/* The "heard from" chips the onboarding question offers, by the source the site recorded. One copy,
   shared with the wizard: the answer and the prefill have to be comparable, and the server stores what
   was offered so a kept prefill is not read as independent evidence (§4.2.4). */
export const HEARD_FROM_BY_UTM_SOURCE = {
  google: 'google',
  adwords: 'google',
  bing: 'google',
  linkedin: 'linkedin',
  youtube: 'youtube',
  facebook: 'facebook',
  instagram: 'facebook',
  meta: 'facebook',
  x: 'x',
  twitter: 'x',
  reddit: 'reddit',
  newsletter: 'newsletter',
  n8n: 'marketplace',
  zapier: 'marketplace',
  make: 'marketplace',
  marketplace: 'marketplace',
  claude: 'ai_directory',
  anthropic: 'ai_directory',
  chatgpt: 'ai_directory',
  openai: 'ai_directory',
  referral: 'friend',
};

function cookie(name) {
  try {
    return Vue.$cookies ? Vue.$cookies.get(name) : null;
  } catch (err) {
    return null;
  }
}

/** `ini_attr` is URL-encoded JSON; vue-cookies decodes and parses it, but never trust the shape. */
function attrCookie() {
  const raw = cookie(ATTR_COOKIE);
  if (!raw) return {};
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (err) {
    return {};
  }
}

/** True when the browser sits in a time zone the site treats as European (its opt-in region). */
function inEuropeanTimeZone() {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return zone.indexOf('Europe/') === 0;
  } catch (err) {
    return false;
  }
}

/**
 * What the visitor allowed. `ini_consent` looks like `analytics=1&ads=0&at=2026-09-13`; without it the
 * site's default applies, and the app must never send more than the site itself would have.
 */
export function readConsent() {
  const raw = cookie(CONSENT_COOKIE);
  if (!raw || typeof raw !== 'string') {
    const granted = !inEuropeanTimeZone();
    return { analytics: granted, ads: granted, source: 'default' };
  }

  const values = {};
  String(raw).split('&').forEach((pair) => {
    const [key, value] = pair.split('=');
    if (key) values[key.trim()] = (value || '').trim();
  });

  return { analytics: values.analytics === '1', ads: values.ads === '1', source: 'cookie' };
}

/** Both query positions occur: `/?utm_source=x#/createAccount` and `#/createAccount?utm_source=x`. */
function queryParams() {
  const found = {};
  const collect = (search) => {
    if (!search) return;
    String(search).replace(/^[?]/, '').split('&').forEach((pair) => {
      if (!pair) return;
      const index = pair.indexOf('=');
      const key = decodeURIComponent(index < 0 ? pair : pair.slice(0, index));
      const value = index < 0 ? '' : decodeURIComponent(pair.slice(index + 1).replace(/\+/g, ' '));
      if (key && found[key] === undefined) found[key] = value;
    });
  };

  try {
    collect(window.location.search);
    const hash = window.location.hash || '';
    const mark = hash.indexOf('?');
    if (mark >= 0) collect(hash.slice(mark));
  } catch (err) {
    /* no window — nothing to read */
  }

  return found;
}

/** Host plus path, as the site records a referrer; our own site is not a referrer. */
function externalReferrer() {
  try {
    const raw = document.referrer;
    if (!raw) return null;
    const url = new URL(raw);
    if (/(^|\.)inistate\.com$/i.test(url.hostname)) return null;
    return (url.hostname + url.pathname).replace(/\/$/, '') || url.hostname;
  } catch (err) {
    return null;
  }
}

function readStoredAppTouch() {
  try {
    const raw = window.localStorage.getItem(APP_TOUCH_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    if (!stored || !stored.at) return null;
    const age = Date.now() - new Date(stored.at).getTime();
    if (age > APP_TOUCH_DAYS * 24 * 60 * 60 * 1000) return null;
    return stored;
  } catch (err) {
    return null;
  }
}

/**
 * Records the app's own touch when this visit carries one: a link straight to the app, which the
 * marketing site never saw. Call it early; `signupAttribution` calls it too, so a person who signs up
 * on the same page load is covered either way. Only stored under analytics consent.
 */
export function rememberAppTouch() {
  const params = queryParams();
  const touch = {};

  UTM_KEYS.forEach((key) => {
    if (params[key]) touch[key] = params[key];
  });

  const referrer = externalReferrer();
  if (referrer) touch.referrer = referrer;

  if (!Object.keys(touch).length) return null;

  try {
    touch.landing_page = window.location.pathname + (window.location.hash || '');
  } catch (err) {
    /* no window */
  }
  touch.at = new Date().toISOString();

  if (!readConsent().analytics) return null;

  try {
    // The newest touch replaces the older one: it is what brought them here now, and whatever the
    // earlier one produced has already been reported with it.
    window.localStorage.setItem(APP_TOUCH_KEY, JSON.stringify(touch));
  } catch (err) {
    /* private mode — the touch lives for this page load only */
  }

  return touch;
}

/** `_ga` is `GA1.1.<id>.<timestamp>`; GA4 wants the part after the prefix. */
function gaClientId() {
  const raw = cookie(GA_COOKIE);
  if (!raw || typeof raw !== 'string') return null;
  const match = String(raw).match(/^GA\d\.\d\.(.+)$/);
  return match ? match[1] : null;
}

/** `_ga_<id>` is `GS2.1.s1789271571$o1$…`; the session id is the `s…` segment. */
function gaSessionId() {
  const raw = cookie(GA_SESSION_COOKIE);
  if (!raw || typeof raw !== 'string') return null;
  const match = String(raw).match(/s(\d+)/);
  return match ? match[1] : null;
}

/**
 * Everything the server needs to know where this sign-up came from. Safe to call on any page, at any
 * time: it reads, never writes (beyond remembering the app's own touch), and returns `{}`-shaped data
 * with whatever it could find.
 */
export function signupAttribution() {
  const attribution = {};

  try {
    const consent = readConsent();
    const appTouch = rememberAppTouch() || readStoredAppTouch();
    const cookieAttr = attrCookie();
    const params = queryParams();

    if (cookieAttr.first) attribution.first = cookieAttr.first;
    if (cookieAttr.last) attribution.last = cookieAttr.last;
    if (appTouch) attribution.app = appTouch;

    const solution = params.solution || params.solutionId;
    if (solution) attribution.intent = { solution };

    // The MCP flow is an ordinary sign-up carrying the connector's name; only this client knows it.
    if (params.mcp_client_name) attribution.connector = { clientName: params.mcp_client_name };

    const reference = cookie(REFERENCE_COOKIE) || params.ref;
    if (reference) attribution.reference = reference;

    const prefill = heardFromPrefill(attribution);
    if (prefill) attribution.heardFromPrefill = prefill;

    const ids = {};
    if (consent.analytics) {
      const client = gaClientId();
      const session = gaSessionId();
      if (client) ids.gaClientId = client;
      if (session) ids.gaSessionId = session;
    }
    if (consent.ads) {
      const fbp = cookie(FBP_COOKIE);
      const fbc = cookie(FBC_COOKIE);
      if (fbp) ids.fbp = fbp;
      if (fbc) ids.fbc = fbc;
    }
    if (Object.keys(ids).length) attribution.ids = ids;

    attribution.consent = consent;
  } catch (err) {
    // Whatever went wrong, a sign-up must still be possible.
    console.warn('[Attribution]', err);
  }

  return attribution;
}

/**
 * The "heard from" chip a source suggests, or null. The wizard starts its question here, and the
 * sign-up sends the same value so the server can tell a kept prefill from a chosen answer.
 */
export function heardFromPrefill(attribution) {
  const touch = (attribution && (attribution.app || attribution.last || attribution.first)) || {};
  const source = String(touch.utm_source || '').toLowerCase();
  return HEARD_FROM_BY_UTM_SOURCE[source] || null;
}

export default { signupAttribution, heardFromPrefill, readConsent, rememberAppTouch, HEARD_FROM_BY_UTM_SOURCE };
